import React, { useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Button, Card, EmptyState, Table, Tabs } from "@opendate-app/setlist";
import { availabilityWindowShape } from "./availabilityFormatters";

const initials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const FILTERS = {
  all: { label: "Everyone", keep: () => true },
  none: { label: "Not answered", keep: (person) => person.answered_count === 0 },
  notes: { label: "With notes", keep: (person) => Boolean(person.note) },
};

const AvailabilityByPerson = ({ availabilityWindow, reminded, canRemind, busy, onRemind }) => {
  const [filter, setFilter] = useState("all");
  const { people, stats } = availabilityWindow;
  const rows = people.filter(FILTERS[filter].keep);

  const columns = [
    {
      key: "name",
      header: "Staff member",
      primary: true,
      render: (person) => (
        <span className="d-inline-flex align-items-center" style={{ gap: 10 }}>
          <Avatar size="xs" initials={initials(person.name)} alt={person.name} />
          {person.name}
        </span>
      ),
    },
    { key: "positions", header: "Positions", render: (person) => person.position_names.join(", ") },
    {
      key: "answered",
      header: "Answered",
      render: (person) => (
        <span style={{ color: person.answered_count < stats.show_count ? "var(--sl-text-warning)" : "var(--sl-text-tertiary)", fontWeight: 500 }}>
          {person.answered_count} of {stats.show_count}
        </span>
      ),
    },
    {
      key: "available",
      header: "Available for",
      render: (person) => (person.answered_count ? `${person.available_count} ${person.available_count === 1 ? "show" : "shows"}` : "—"),
    },
    { key: "note", header: "Notes", render: (person) => <span style={{ color: "var(--sl-text-tertiary)" }}>{person.note || ""}</span> },
    {
      key: "remind",
      header: "",
      align: "right",
      render: (person) => {
        if (!canRemind || !person.remindable || person.answered_count >= stats.show_count) return null;
        if (reminded[person.staff_member_id]) return <span style={{ fontSize: 13, color: "var(--sl-text-tertiary)" }}>Reminded just now</span>;
        return (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onRemind(person)}>
            Remind
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <div className="mb-3">
        <Tabs
          variant="button-minimal"
          collapseBelow={0}
          aria-label="Filter staff"
          items={Object.entries(FILTERS).map(([value, { label, keep }]) => ({ value, label: `${label} (${people.filter(keep).length})` }))}
          value={filter}
          onChange={setFilter}
        />
      </div>
      <Card padding="none">
        <Table
          columns={columns}
          data={rows}
          getRowKey={(person) => person.staff_member_id}
          stackBelow={720}
          empty={<EmptyState size="sm" showRings={false} title="Nobody here" description="Try another filter." />}
        />
      </Card>
    </>
  );
};

AvailabilityByPerson.propTypes = {
  availabilityWindow: availabilityWindowShape.isRequired,
  reminded: PropTypes.objectOf(PropTypes.bool).isRequired,
  canRemind: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  onRemind: PropTypes.func.isRequired,
};

export default AvailabilityByPerson;
